import { createContext, useState } from 'react';

type VocabDict = { [key: string]: boolean };
type VocabSubrequestDict = { [key: string]: VocabDict };

type Methods = {
  isVocabRequested: (key: string, subrequest?: string) => boolean;
  setVocabRequested: (key: string, subrequest?: string) => void;
  resetContext: () => void;
};

/**
 * Context for managing vocabulary request state.
 * @context VocabContext
 * @property {(vocabName, subrequest=undefined) => boolean} isVocabRequested
 * - Function that returns whether vocab of the given name has been requested
 * @property {(vocabName, subrequest=undefined) => void} setVocabRequested
 * - Function to set the vocab of the given name as requested.
 * Important to note: this will **not** trigger a re-render of the component tree.
 * @property {() => void} resetContext
 * - Function to reset the context to initial state.
 * This will trigger a re-render of the component tree.
 * NOTE we don't use this atm, it's just added (and tested) for completeness.
 * It will probably need amendments if we need it for some future use case.
 */
export const VocabContext = createContext<Methods>({
  isVocabRequested: () => false,
  setVocabRequested: () => {},
  resetContext: () => {},
});

export const VocabProvider = ({ children }: { children: React.ReactNode }) => {
  const [value, setValue] = useState<VocabDict>({});
  const [subValue, setSubValue] = useState<VocabSubrequestDict>({});
  const isVocabRequested = (key: string, subrequest?: string) =>
    (subrequest !== undefined ? subValue[subrequest]?.[key] : value[key]) ||
    false;
  const setVocabRequested = (key: string, subrequest?: string) => {
    // Do **not** setValue as it would trigger the
    // re-render of the component tree,
    // which we explicitly want to avoid.
    if (subrequest !== undefined) {
      if (!subValue[subrequest]) {
        subValue[subrequest] = {};
      }
      subValue[subrequest][key] = true;
    } else {
      value[key] = true;
    }
  };
  const resetContext = () => {
    setValue({});
    setSubValue({});
  };
  return (
    <VocabContext.Provider
      value={{ isVocabRequested, setVocabRequested, resetContext }}
    >
      {children}
    </VocabContext.Provider>
  );
};
